// LOCAL: placeholder page — no merchant-ui counterpart.
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import {
  Action,
  Card,
  Modal,
  Page,
  PageStatus,
  TextInput,
  useToast,
} from 'corso-admin-ui';
import { CommandAdminApi, commandPlatformApi } from 'corso-types';
import { FormEventHandler, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { match, P } from 'ts-pattern';
import { z } from 'zod';
import api from '~/api';
import {
  DataTable,
  DataTableStatus,
  sharedTableDefinitions,
} from '~/components/dataTable';

type Organization = CommandAdminApi.ResponseBody<
  '/organization',
  'get'
>[number];

const createSchema = commandPlatformApi['/organization'].post.request.body;

const { idColumn, nameColumn, storeCountColumn, createdOnColumn } =
  sharedTableDefinitions;

const organizationColumns: ColumnDef<Organization>[] = [
  idColumn<Organization>(),
  nameColumn<Organization>(),
  storeCountColumn<Organization>(),
  createdOnColumn<Organization>(),
];

function useOrganizationCreate() {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (body: z.infer<typeof createSchema>) =>
      api.organizations.create(body),
    onSuccess: () => {
      toast.show('Created organization');
      return queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
    onError: () => toast.show('Failed to create organization'),
  });
}

function CreateOrgForm({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) {
  const {
    formState: { errors },
    handleSubmit,
    reset,
    register,
  } = useForm({
    resolver: zodResolver(createSchema),
  });

  const { mutate: createOrganization } = useOrganizationCreate();

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit((formValues) => {
      createOrganization(formValues);
      reset();
      onClose();
    })(event).catch(console.error);
  };

  return (
    <Modal show={show} onClose={onClose} title="Create Organization">
      <form className="flex flex-col gap-6" onSubmit={submitHandler}>
        <TextInput
          id="name"
          label="Name"
          details="The name of the organization."
          required
          {...register('name')}
          error={errors.name?.message}
        />
        <Action variant="primary" type="submit">
          Create
        </Action>
      </form>
    </Modal>
  );
}

export default function AllOrganizationsPage() {
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);

  const query = useQuery({
    queryKey: ['organizations'],
    queryFn: () => api.organizations.list(),
  });
  return (
    <Page
      size="fullWidth"
      title="Organizations"
      primaryAction={{
        onAction: () => setShowCreate(true),
        content: 'Create Organization',
      }}
    >
      {match(query)
        .with({ isPending: true }, () => (
          <PageStatus.Loading title="Loading organizations" />
        ))
        .with({ isError: true }, () => (
          <PageStatus.Error
            title="Failed to load organizations"
            error={query.error}
          />
        ))
        .with({ isSuccess: true, data: P.select() }, (data) =>
          data.length > 0 ? (
            <Card>
              <DataTable
                data={data}
                columns={organizationColumns}
                onRowClick={(org) => navigate(`${org.id}`)}
                filterable
              />
            </Card>
          ) : (
            <DataTableStatus
              icon="store"
              title="No organizations found"
              details="Create an organization to get started."
            />
          ),
        )
        .exhaustive()}
      <CreateOrgForm show={showCreate} onClose={() => setShowCreate(false)} />
    </Page>
  );
}
